import { v4 as uuidv4 } from 'uuid';
import { CreditProductsApi, UserApi } from '~/api';
import {
  action,
  makeObservable,
  observable,
  persist,
  toOnlyNumbers,
} from '~/modules';
import { Storage } from '~/services';
import { RootStore } from '~/stores';
import {
  type ACCOUNT_STATUS,
  type DOC_TYPES,
  QR_CODE_STATUS,
  uploadFileToAzure,
} from '~/utils';

export default class UserStore {
  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);
  }

  @observable
  rootStore: RootStore;

  @persist('object')
  @observable
  selectedDocumentType: DOC_TYPES | null = null;

  @persist('object')
  @observable
  addressData: AddressData = {
    cep: '',
    city: '',
    neighborhood: '',
    street: '',
    uf: '',
    complement: '',
    number: '',
  };

  @persist('object')
  @observable
  mobilePhone: string | null = null;

  @persist('object')
  @observable
  geoLocationData: GeoLocationData = {
    latitude: 0,
    longitude: 0,
    altitude: 0,
    accuracy: 0,
  };

  @persist('object')
  @observable
  termsAndPolicy = {
    regulatoryDocuments: [],
  } as TermsAndPolicy;

  @persist('object')
  @observable
  qrCodeId: string | null = null;

  @action
  setSelectedDocumentType = (docType: DOC_TYPES) => {
    this.selectedDocumentType = docType;
  };

  @action
  updateAddressData = async (data: Partial<AddressData>): Promise<void> => {
    this.addressData = { ...this.addressData, ...data };
  };

  @action
  setTermsAndPolicy = (termsAndPolicy: TermsAndPolicy) => {
    this.termsAndPolicy = termsAndPolicy;
  };

  @action
  setMobilePhone = (mobilePhone: string) => {
    this.mobilePhone = mobilePhone;
  };

  @action
  setGeoLocationData = (data: GeoLocationData) => {
    this.geoLocationData = data;
  };

  @action
  authenticate = async (
    id: string,
  ): Promise<{
    tokenStatus: QR_CODE_STATUS;
    accountStatus: ACCOUNT_STATUS;
  }> => {
    const { access_token } = await UserApi.getPublicAccessToken();
    Storage.setToken({ accessToken: access_token }); // Set public access token
    const { accessToken, status, accountStatus } =
      await UserApi.getPrivateAccessToken(id);
    Storage.setToken({ accessToken }); // Set private access token
    return { tokenStatus: status, accountStatus };
  };

  @action
  generateMockQrCode = async ({ document }: { document: string }) => {
    const { access_token } = await UserApi.getPublicAccessToken();
    Storage.setToken({ accessToken: access_token }); // Set public access token
    await CreditProductsApi.generateQRCode(document);
  };

  @action
  fetchUserData = async (): Promise<UserData> => {
    return await UserApi.fetchUserData();
  };

  @action
  getTermsAndPolicy = async () => {
    const termsAndPolicy = await UserApi.getTermsAndPolicy();
    this.setTermsAndPolicy(termsAndPolicy);
  };

  @action
  getAddressByCEP = async (): Promise<void> => {
    const { cep } = this.addressData;
    const data = await UserApi.getAddressByCEP(toOnlyNumbers(cep || ''));
    this.updateAddressData(data);
  };

  @action
  updateCreditOnboardingUserAccount = async (): Promise<void> => {
    const uniqueId = uuidv4();
    const payload = {
      agreeTerms: uniqueId,
      mobilePhone: this.mobilePhone || '',
      address: {
        cep: toOnlyNumbers(this.addressData.cep || ''),
        address: this.addressData.street,
        number: this.addressData.number,
        city: this.addressData.city,
        neighborhood: this.addressData.neighborhood,
        uf: this.addressData.uf,
        country: 'BR',
      },
      appInfo: {
        ...this.geoLocationData,
      },
    };

    await UserApi.updateCreditOnboardingUserAccount(payload);
  };

  @action
  userImageValidation = async (file: {
    uri: string;
    name: string;
    mimeType: string;
    blob: Blob;
    type: DOC_TYPES;
  }): Promise<void> => {
    await uploadFileToAzure({
      blob: file.blob,
      name: file.name,
      type: file.mimeType,
    });
    await UserApi.userImageValidation({
      filename: file.name,
      type: file.type,
      contentType: file.mimeType,
    });
  };

  @action
  acceptContract = async (): Promise<void> => {
    await UserApi.acceptContract();
  };
}
